import _ from 'lodash'
import moment from 'moment-timezone'
import { createSelector } from 'reselect'
import { Dispatch } from 'redux'
import { WithApolloClient } from '@apollo/client/react/hoc'

import { DefaultRootState } from '~/types'
import { isPartProcessing } from '~/lib/partStates'
import isFlagEnabled from '~/lib/isFlagEnabled'
import { IdentityType } from '~/redux-modules/identity/type'
import { getUserIdentity } from '~/redux-modules/identity'
import {
    coreQuoteDetails_quote_quoteModels_items as CoreQuoteModelsItemsType,
    coreQuoteDetails_quote_quoteAttachments_items as CoreQuoteAttachmentItemsType,
} from '~/redux-modules/query-refactor/__generated__/coreQuoteDetails'
import Subset from '~/apps/consumer/helpers/Subset'
import {
    QuoteDetails_Quote_QuoteModels_ItemFragment as quoteModelType,
    QuoteDetails_Quote_Attachments_ItemFragment as quoteAttachmentType,
    QuoteDetails_Quote_QuoteModels_Item_SelectedQuoteModelRevision_AttachmentLinkFragment as AttachmentLinkType,
    QuoteDetailsQuery,
    QuoteDetails_Quote_Attachments_ItemFragment,
    QuoteDetails_Quote_QuoteModels_ItemFragment,
} from '~/gql/graphql'
import { quoteDetailsPageSelector } from '~/redux-modules/query-refactor/selector'
import { PropsCustomWithRouter } from '~/lib/customWithRouter'

import {
    ATTACHMENT_PREFIX,
    MODEL_PREFIX,
    QUOTE_MODEL_REVISION_ATTACHMENT,
    PDF_OCR_ANALYSIS_TIMEOUT_MINUTES,
    ALLOWED_MINUTES_FOR_PROCESSING,
} from './constants'
import { isConfiguredWithCustomMaterial } from './FilesTab/selectors'
import { PROVISIONED_UI_FLAGS } from './logic'

export interface QuoteDetailsDispatchToProps {
    getCoreQuoteDetailsData: () => void
    coreQuoteDetailsQueryReset: () => void
    getLegacyQuoteDetailsData: () => void
    getBusinessAccountFeatureData: (id: string) => void
    dispatch: Dispatch
}
export interface QuoteDetailsStateToProps {
    identity: IdentityType
    coreQuoteData: ReturnType<typeof quoteDetailsPageSelector>
    isQLLTOptimizationOn: boolean
    isImMoldRepairEnabled: boolean
    isCpqFlagEnabled: boolean
}

export interface QuoteDetailsOwnProps {
    /**
     * @deprecated - couldn't find any place where we pass this property to QuoteDetail component. Maybe we don't need at all?
     */
    isImQuoteType?: boolean
    quoteId?: string
    isLocatedInAdminPage?: boolean
    isLocatedInTeamPage?: boolean
    userInTeam?: boolean
    userId?: number
    refetchQuery?: any
    revisionId?: string
}

interface QuoteDetailsProps
    extends QuoteDetailsOwnProps,
        QuoteDetailsDispatchToProps,
        QuoteDetailsStateToProps,
        PropsCustomWithRouter {}

export type QuoteDetailsPropsWithClient = WithApolloClient<QuoteDetailsProps>

export const quoteModelRevisionKey = (quoteModelRevisionId: string) => {
    return `${MODEL_PREFIX}${quoteModelRevisionId}`
}

export const attachmentKey = (attachmentId: string) => {
    return `${ATTACHMENT_PREFIX}${attachmentId}`
}

export type TableDataType = {
    key: string
    item:
        | quoteModelType
        | quoteAttachmentType
        | CoreQuoteModelsItemsType
        | CoreQuoteAttachmentItemsType
}

export const getTableData = ({
    data,
}: {
    data: Partial<QuoteDetailsQuery>
}) => {
    const quoteModels = data.quote?.quoteModels?.items || []
    const quoteAttachments = data.quote?.quoteAttachments?.items || []
    const tableData: {
        key: string
        item:
            | QuoteDetails_Quote_Attachments_ItemFragment
            | QuoteDetails_Quote_QuoteModels_ItemFragment
    }[] = []
    const linkedAttachmentRevisionSet = new Set()

    // Round up all the models in this quote
    quoteModels.forEach(item => {
        tableData.push({
            key: quoteModelRevisionKey(item.selectedQuoteModelRevision.id),
            item,
        })
        if (
            item.selectedQuoteModelRevision?.attachmentLink
                ?.quoteAttachmentRevision?.id
        ) {
            linkedAttachmentRevisionSet.add(
                item.selectedQuoteModelRevision.attachmentLink
                    .quoteAttachmentRevision.id,
            )
        }
    })

    // Round up all the models in this quote
    quoteAttachments.forEach(item => {
        if (
            !linkedAttachmentRevisionSet.has(item.latestAttachmentRevision.id)
        ) {
            tableData.push({
                key: attachmentKey(item.id),
                item,
            })
        }
    })

    return tableData
}

export const isRevisionProcessing = (
    item: quoteModelType | CoreQuoteModelsItemsType,
) => {
    if (!isPartProcessing(item?.selectedQuoteModelRevision?.partRevision)) {
        return false
    }

    const updatedAt = moment(
        item.selectedQuoteModelRevision.partRevision.updatedAt,
    )
    const cutoff = updatedAt.add(ALLOWED_MINUTES_FOR_PROCESSING, 'minutes')

    return moment().isBefore(cutoff)
}

export const getCustomMaterialNames = createSelector(
    _.property('quoteModels.items'),
    (quoteModels: quoteModelType[] = []) => {
        quoteModels
            .filter(quoteModel =>
                isConfiguredWithCustomMaterial(
                    quoteModel.selectedQuoteModelRevision,
                ),
            )
            .map(
                quoteModel =>
                    quoteModel?.selectedQuoteModelRevision?.modelConfiguration
                        ?.material?.name,
            )
    },
)

type AttachmentLinkSubset = Subset<
    AttachmentLinkType,
    {
        __typename: string
        createdAt: Date
        isPdfOcrAutoQuotable: boolean | null
    }
>

export const hasProcessingAttachmentByQmr = (
    attachmentLink?: AttachmentLinkSubset | null,
) => {
    if (
        !attachmentLink ||
        attachmentLink?.__typename !== QUOTE_MODEL_REVISION_ATTACHMENT
    ) {
        return false
    }
    const createdAt = moment(attachmentLink?.createdAt)
    const cutoff = createdAt.add(PDF_OCR_ANALYSIS_TIMEOUT_MINUTES, 'minutes')

    if (moment().isAfter(cutoff)) {
        return false
    }

    return Boolean(attachmentLink?.isPdfOcrAutoQuotable == null)
}

export const hasProcessingAttachment = (
    quoteModelItem: quoteModelType | CoreQuoteModelsItemsType,
) => {
    return hasProcessingAttachmentByQmr(
        quoteModelItem.selectedQuoteModelRevision?.attachmentLink,
    )
}
export const selectFlags = (state: DefaultRootState) => {
    const identity = getUserIdentity(state)
    const shouldHideInternationalOrder = isFlagEnabled(
        identity,
        PROVISIONED_UI_FLAGS.HIDE_INTERNATIONAL_ORDERING,
        'true',
    )

    const disableDriftMessaging = isFlagEnabled(
        identity,
        PROVISIONED_UI_FLAGS.DISABLE_DRIFT_MESSAGING,
        'true',
    )

    return {
        shouldHideInternationalOrder,
        disableDriftMessaging,
    }
}

export const getCoreQuoteTableData = (
    quoteModels: CoreQuoteModelsItemsType[] = [],
    quoteAttachments: CoreQuoteAttachmentItemsType[],
) => {
    const data: TableDataType[] = []
    const linkedAttachmentRevisionSet = new Set()

    // Round up all the models in this quote
    quoteModels.forEach(item => {
        data.push({
            key: quoteModelRevisionKey(item.selectedQuoteModelRevision?.id),
            item,
        })
        if (
            item.selectedQuoteModelRevision?.attachmentLink
                ?.quoteAttachmentRevision?.id
        ) {
            linkedAttachmentRevisionSet.add(
                item.selectedQuoteModelRevision?.attachmentLink
                    ?.quoteAttachmentRevision?.id,
            )
        }
    })

    // Round up all the models in this quote
    quoteAttachments.forEach(item => {
        if (
            !linkedAttachmentRevisionSet.has(item.latestAttachmentRevision.id)
        ) {
            data.push({
                key: attachmentKey(item.id),
                item,
            })
        }
    })

    return data
}
